import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { motion } from "framer-motion";

import FnrLoadingBricks from "UI/FnrLoadingBricks";
import FnrModal from "UI/FnrModal";
import {
  SharedResizingChild,
  SharedResizingContainer,
} from "UI/SharedResizingArea2";
import { colors } from "UI/colors";

import { useCloseModal } from "Utilities/Hooks/useCloseModal";

import { useRecurringGifts } from "../RecurringGiftsContext";

import EditPanel from "./EditPanel";
import FundNameTopArea from "./FundNameTopArea";
import InitialPanel from "./InitialPanel";
import PaymentMethodsPanel from "./PaymentMethodsPanel";

EditRecurringGiftModal.propTypes = {
  onModalClose: PropTypes.func,
};

export default function EditRecurringGiftModal({ onModalClose }) {
  const { state, dispatch } = useRecurringGifts();
  const location = useLocation();
  const [show, setShow] = useState(true);
  const { currentPanel } = state.editModal;
  const showCloseButton = currentPanel === "initial";
  const isLoading = !state.editModal.currentGift?.ID;

  const updateURLPathAfterClosing = useCloseModal({
    pathString: "recurring",
    removeTrailingID: true,
  });

  const closeModal = () => {
    setShow(false);

    if (onModalClose) onModalClose();

    setTimeout(() => {
      updateURLPathAfterClosing();
      dispatch({ type: "clearGiftDetails" });
    }, 300);
  };

  useEffect(
    function setCurrentGiftInContext() {
      const pathnameAsArray = location.pathname.split("/");
      const recurringIndex = pathnameAsArray.indexOf("recurring");
      const giftID = pathnameAsArray[recurringIndex + 1];
      const gift = state.recurringGifts.find((g) => g.ID === Number(giftID));

      if (gift) {
        dispatch({
          type: "setCurrentGift",
          payload: gift,
        });
      }

      if (state.recurringGifts.length && !gift) {
        closeModal();
      }
    },
    // eslint-disable-next-line
    [location, dispatch, state.recurringGifts]
  );

  return (
    <FnrModal show={show}>
      <Wrapper>
        {showCloseButton ? (
          <CloseButton
            data-cy="closeEditRecurring"
            role="button"
            onClick={closeModal}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </CloseButton>
        ) : null}

        <SharedResizingContainer>
          <SharedResizingChild resizeKey="loading" show={isLoading} side="left">
            <Loading>
              <FnrLoadingBricks show size={38} />
            </Loading>
          </SharedResizingChild>

          <SharedResizingChild
            resizeKey="initial"
            show={!isLoading && currentPanel === "initial"}
            side={isLoading ? "right" : "left"}
          >
            <InitialPanel closeModal={closeModal} />
          </SharedResizingChild>

          <SharedResizingChild
            resizeKey="edit"
            side={currentPanel === "initial" ? "right" : "left"}
            show={currentPanel === "edit"}
          >
            <EditPanel />
          </SharedResizingChild>

          <SharedResizingChild
            resizeKey="paymentMethods"
            side="right"
            show={currentPanel === "paymentMethods"}
          >
            <PaymentMethodsPanel />
          </SharedResizingChild>
        </SharedResizingContainer>
      </Wrapper>
    </FnrModal>
  );
}

const Wrapper = styled(motion.div)`
  max-width: 90vw;
  width: 500px;
  background-color: white;
  //padding: 20px;
  border-radius: 20px;
  border: 1px solid #bec3c7;
  font-size: 16px;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;

  @media (max-width: 550px) {
    max-width: 85vw;
  }
`;

const CloseButton = styled.div`
  z-index: 2;
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 24px;
  cursor: pointer;
  color: #c3c3c3;
  transition: color 200ms;

  &:hover {
    color: ${colors.fnBlue};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const Loading = styled.div`
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
